import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView, useReducedMotion, AnimatePresence } from 'motion/react';
import {
  MessageSquare,
  Calendar,
  Bell,
  Check,
  Phone,
  Mail,
  Clock,
  Shield,
  ArrowRight,
  Play,
  Menu,
  X,
  ChevronDown,
  Loader2,
  User,
  FileText,
  Star,
  Globe,
  Zap,
  TrendingUp,
  Users } from
'lucide-react';

// Smooth scroll utility with offset for sticky nav
const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    const offset = 120; // Account for sticky nav
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  }
};

// Count up component
const CountUp = ({ end, duration = 2, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  const shouldReduceMotion = useReducedMotion();
  const BOOKING_URL = "https://calendar.app.google/SwKPTVcDYqL52xw98";

  const openBooking = () => {
    window.open(BOOKING_URL, "_blank", "noopener,noreferrer");
  };


  useEffect(() => {
    if (isInView && !hasAnimated) {
      if (shouldReduceMotion) {
        setCount(end);
        setHasAnimated(true);
        return;
      }
      setHasAnimated(true);
      let start = 0;
      const increment = end / (duration * 60);
      const timer = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(timer);
        } else {
          setCount(Math.floor(start));
        }
      }, 1000 / 60);
      return () => clearInterval(timer);
    }
  }, [isInView, end, duration, hasAnimated, shouldReduceMotion]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Animated section wrapper
const AnimatedSection = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const shouldReduceMotion = useReducedMotion();

  if (shouldReduceMotion) {
    return <div ref={ref}>{children}</div>;
  }

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
      transition={{ duration: 0.5, delay, ease: [0.22, 1, 0.36, 1] }}>

      {children}
    </motion.div>);

};

// FAQ Accordion Item
const FAQItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="faq-item">
      <button className="faq-question" onClick={onClick}>
        <span>{question}</span>
        <ChevronDown size={20} className={`faq-icon ${isOpen ? 'open' : ''}`} />
      </button>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
        <p>{answer}</p>
      </div>
    </div>);

};

// Modal Component
const Modal = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <motion.div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.2 }}>

        <div className="modal-header">
          <h2 className="modal-title">{title}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close modal">
            <X size={24} />
          </button>
        </div>
        <div className="modal-body">
          {children}
        </div>
      </motion.div>
    </div>);

};

const LandingPage = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(null);
  const [activeModal, setActiveModal] = useState(null);
  const [formData, setFormData] = useState({
    fullName: '',
    clinicName: '',
    email: '',
    whatsapp: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const shouldReduceMotion = useReducedMotion();

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNavClick = (sectionId) => {
    scrollToSection(sectionId);
    closeMenu();
  };

  const faqData = [
  {
    question: "How long does setup take?",
    answer: "We typically complete setup in 48-72 hours. This includes mapping your clinic workflows, connecting WhatsApp and website chat, and testing the system before launch."
  },
  {
    question: "What channels does it work with?",
    answer: "BookingReply integrates with WhatsApp, website chat widgets, and can connect to your booking calendar (Google Calendar, Calendly, or custom systems)."
  },
  {
    question: "Is this suitable for sensitive messages?",
    answer: "BookingReply is designed for general administrative communication like appointment booking, FAQs, and reminders. For sensitive medical discussions or patient records, we recommend using your clinic's secure communication channels."
  },
  {
    question: "Can I customize the responses?",
    answer: "Absolutely. We tailor all conversations to your clinic's services, pricing, hours, and tone. You can request changes anytime, and we optimize scripts weekly."
  },
  {
    question: "What about patient data and privacy?",
    answer: "We follow best practices for data handling and work within your existing privacy framework. The system doesn't store sensitive medical information—it focuses on scheduling and general inquiries."
  },
  {
    question: "Is there a contract or minimum commitment?",
    answer: "No contracts required. We offer flexible month-to-month plans so you can scale as needed without long-term commitments."
  }];


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePhone = (phone) => {
    return /^[+]?[(]?[0-9]{1,4}[)]?[-\s.]?[(]?[0-9]{1,4}[)]?[-\s.]?[0-9]{1,9}$/.test(phone);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ type: '', message: '' });

    // Validation
    if (!formData.fullName.trim()) {
      setFormStatus({ type: 'error', message: 'Please enter your full name' });
      return;
    }
    if (!formData.clinicName.trim()) {
      setFormStatus({ type: 'error', message: 'Please enter your clinic name' });
      return;
    }
    if (!validateEmail(formData.email)) {
      setFormStatus({ type: 'error', message: 'Please enter a valid email address' });
      return;
    }
    if (!validatePhone(formData.whatsapp)) {
      setFormStatus({ type: 'error', message: 'Please enter a valid WhatsApp number' });
      return;
    }

 setIsSubmitting(true);

try {
  const FORMSPREE_URL = "https://formspree.io/f/xgooeloa"; // your endpoint
const res = await fetch(FORMSPREE_URL, {
  method: "POST",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
  body: JSON.stringify(formData),
});

if (!res.ok) {
  let details = "";
  try {
    const data = await res.json();
    details = data?.error || JSON.stringify(data);
  } catch (e) {}
  throw new Error(details || "Form submission failed");
}

// ✅ Also send to Google Sheets (free) — reliable GET ping
const SHEETS_WEBAPP_URL =
  "https://script.google.com/macros/s/AKfycbyp5Yrt5YsLduKe1tbcsPDQhCZMtKM_uR8ewEGQ2kmcr9N6mycp2BW1Hsw01BeZlBBU/exec";

const qs = new URLSearchParams({
  fullName: formData.fullName || "",
  clinicName: formData.clinicName || "",
  email: formData.email || "",
  whatsapp: formData.whatsapp || "",
  message: formData.message || "",
  source: "bookingreply.com",
  _ts: String(Date.now()),
}).toString();

const img = new Image();
img.src = `${SHEETS_WEBAPP_URL}?${qs}`;

  setFormStatus({
  type: "success",
  message: "Thanks! Now pick a time for your demo (opening calendar)...",
});

// Give the Sheets request a moment to fire, then send them to the booking page


  setFormData({
    fullName: "",
    clinicName: "",
    email: "",
    whatsapp: "",
    message: "",
  });
} catch (error) {
  console.error("Form submit error:", error);
  setFormStatus({
    type: "error",
    message: "Something went wrong. Please try again or email us directly.",
  });
} finally {
  setIsSubmitting(false);
}
};


  return (
    <div className="landing-page">
      {/* Floating Navigation */}
      <nav className="floating-nav">
        <div className="nav-container">
          <div className="nav-logo">BookingReply</div>
          
          <div className="nav-links-desktop">
            <a href="#features" onClick={(e) => {e.preventDefault();scrollToSection('features');}}>Features</a>
            <a href="#how-it-works" onClick={(e) => {e.preventDefault();scrollToSection('how-it-works');}}>How it works</a>
            <a href="#results" onClick={(e) => {e.preventDefault();scrollToSection('results');}}>Results</a>
            <a href="#integrations" onClick={(e) => {e.preventDefault();scrollToSection('integrations');}}>Integrations</a>
            <a href="#faq" onClick={(e) => {e.preventDefault();scrollToSection('faq');}}>FAQ</a>
          </div>

          <button
            className="btn-primary btn-nav-cta"
            onClick={() => scrollToSection('demo')}>

            Get Free Demo
          </button>

          <button
            className="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}>

            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen &&
        <motion.div
          className="mobile-menu-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={closeMenu}>

            <motion.div
            className="mobile-menu-panel"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}>

              <div className="mobile-menu-header">
                <span className="mobile-menu-logo">BookingReply</span>
                <button
                className="mobile-menu-close"
                onClick={closeMenu}
                aria-label="Close menu">

                  <X size={24} />
                </button>
              </div>
              <nav className="mobile-menu-links">
                <a href="#features" onClick={(e) => {e.preventDefault();handleNavClick('features');}}>Features</a>
                <a href="#how-it-works" onClick={(e) => {e.preventDefault();handleNavClick('how-it-works');}}>How it works</a>
                <a href="#results" onClick={(e) => {e.preventDefault();handleNavClick('results');}}>Results</a>
                <a href="#integrations" onClick={(e) => {e.preventDefault();handleNavClick('integrations');}}>Integrations</a>
                <a href="#faq" onClick={(e) => {e.preventDefault();handleNavClick('faq');}}>FAQ</a>
                <button
                className="btn-primary mobile-menu-cta"
                onClick={() => {handleNavClick('demo');}}>

                  Get Free Demo
                </button>
              </nav>
            </motion.div>
          </motion.div>
        }
      </AnimatePresence>

      {/* Hero Section */}
      <section className="hero-section-new">
        <div className="container">
          <div className="hero-grid-new">
            <motion.div
              className="hero-content"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>

              <div className="hero-eyebrow">AI-POWERED FRONT DESK</div>
              <h1 className="hero-title-new">
                Your clinic's AI receptionist — on WhatsApp and your website.
              </h1>
              <p className="hero-subtitle-new">
                We install your AI receptionist, connect WhatsApp + your website, and launch a working system in 48–72 hours.
              </p>
              <div className="hero-cta-buttons">
                <button
                  className="btn-primary btn-hero"
                  onClick={() => window.open("https://calendar.app.google/SwKPTVcDYqL52xw98", "_blank", "noopener,noreferrer")}>

                  Get Free Demo <ArrowRight size={20} />
                </button>
                <button
                  className="btn-secondary btn-hero"
                  onClick={() => scrollToSection('how-it-works')}>

                  <Play size={18} /> See how it works
                </button>
              </div>
              <div className="trust-badges">
                <span className="trust-badge"><Check size={14} /> Built for busy front desks</span>
                <span className="trust-badge"><Clock size={14} /> Works 24/7</span>
                <span className="trust-badge"><Star size={14} /> Setup in days</span>
              </div>
            </motion.div>

            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}>

              <div className="ai-collage">
                <div className="collage-card card-main">
                  <div className="card-header">
                    <div className="header-avatar"></div>
                    <div className="header-text">
                      <div className="header-line"></div>
                      <div className="header-line short"></div>
                    </div>
                  </div>
                  <div className="card-body">
                    <div className="chat-bubble incoming">
                      <User size={12} className="bubble-icon" />
                      <div className="bubble-content"></div>
                    </div>
                    <div className="chat-bubble outgoing">
                      <MessageSquare size={12} className="bubble-icon" />
                      <div className="bubble-content short"></div>
                    </div>
                    <div className="chat-bubble incoming">
                      <User size={12} className="bubble-icon" />
                      <div className="bubble-content"></div>
                    </div>
                    <div className="card-actions">
                      <div className="action-chip"><Calendar size={10} /></div>
                      <div className="action-chip"><FileText size={10} /></div>
                    </div>
                  </div>
                </div>
                <motion.div
                  className="collage-chip chip-1"
                  animate={shouldReduceMotion ? {} : { y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}>

                  <Calendar size={14} />
                  <span>Booking</span>
                </motion.div>
                <motion.div
                  className="collage-chip chip-2"
                  animate={shouldReduceMotion ? {} : { y: [0, -8, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}>

                  <MessageSquare size={14} />
                  <span>Reply in 2s</span>
                </motion.div>
                <motion.div
                  className="collage-metric"
                  animate={shouldReduceMotion ? {} : { y: [0, -12, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}>

                  <div className="metric-value">+32%</div>
                  <div className="metric-label">conversion</div>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <AnimatedSection>
        <section className="credibility-strip">
          <div className="container">
            <div className="credibility-items">
              <div className="credibility-item">
                <Shield size={18} />
                <span>HIPAA/GDPR-ready processes</span>
              </div>
              <div className="credibility-divider"></div>
              <div className="credibility-item">
                <Check size={18} />
                <span>Works with your existing workflow</span>
              </div>
              <div className="credibility-divider"></div>
              <div className="credibility-item">
                <Clock size={18} />
                <span>Setup in 48–72h</span>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Section */}
      <AnimatedSection delay={0.1}>
        <section id="features" className="features-section-new">
          <div className="container">
            <div className="section-eyebrow">WHAT IT DOES</div>
            <h2 className="section-heading">Everything your front desk does — automated.</h2>
            <div className="features-grid-new">
              <div className="feature-card-new">
                <div className="feature-icon-new">
                  <MessageSquare size={28} />
                </div>
                <h3 className="feature-title">24/7 Patient Replies</h3>
                <p className="feature-desc">Instant responses to common questions, any time of day or night.</p>
                <ul className="feature-list-new">
                  <li><Check size={16} /> Pricing & services FAQs</li>
                  <li><Check size={16} /> Insurance / availability info</li>
                  <li><Check size={16} /> Directions & contact details</li>
                </ul>
              </div>

              <div className="feature-card-new">
                <div className="feature-icon-new">
                  <Calendar size={28} />
                </div>
                <h3 className="feature-title">Instant Appointment Booking</h3>
                <p className="feature-desc">Seamless scheduling without endless back-and-forth messages.</p>
                <ul className="feature-list-new">
                  <li><Check size={16} /> Captures name + reason</li>
                  <li><Check size={16} /> Suggests times / records preference</li>
                  <li><Check size={16} /> Sends confirmation message</li>
                </ul>
              </div>

              <div className="feature-card-new">
                <div className="feature-icon-new">
                  <Bell size={28} />
                </div>
                <h3 className="feature-title">Reminders & Follow-ups</h3>
                <p className="feature-desc">Keep patients engaged throughout their care journey.</p>
                <ul className="feature-list-new">
                  <li><Check size={16} /> Confirmation after booking</li>
                  <li><Check size={16} /> Reminder before appointment</li>
                  <li><Check size={16} /> Rebooking follow-up</li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* How It Works */}
      <AnimatedSection delay={0.15}>
        <section id="how-it-works" className="how-section-new">
          <div className="container">
            <div className="how-two-column">
              <motion.div
                className="how-left"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}>

                <h2 className="how-main-heading">Set up in 3 simple steps.</h2>
                <p className="how-intro-text">
                  We handle everything from mapping your workflows to launching your AI receptionist. No technical setup required on your end.
                </p>
                <ul className="how-checklist">
                  <li><Check size={18} /> No contracts / cancel anytime</li>
                  <li><Check size={18} /> Works with WhatsApp + website</li>
                  <li><Check size={18} /> Tailored to your clinic</li>
                </ul>
              </motion.div>
              
              <motion.div
                className="how-right"
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}>

                <div className="timeline-container">
                  <div className="timeline-line"></div>
                  
                  <motion.div
                    className="timeline-step"
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}>

                    <div className="timeline-number">01</div>
                    <div className="timeline-card">
                      <h4>We map your clinic</h4>
                      <p>You send services, FAQs, hours, and booking flow. We tailor the assistant to your clinic.</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    className="timeline-step"
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}>

                    <div className="timeline-number">02</div>
                    <div className="timeline-card">
                      <h4>We connect channels</h4>
                      <p>We connect WhatsApp and your website chat so patients get instant replies and next steps.</p>
                    </div>
                  </motion.div>
                  
                  <motion.div
                    className="timeline-step"
                    whileHover={{ y: -4 }}
                    transition={{ duration: 0.2 }}>

                    <div className="timeline-number">03</div>
                    <div className="timeline-card">
                      <h4>Launch + optimize</h4>
                      <p>We launch, test real conversations, and fine-tune the assistant for accuracy.</p>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Results Section */}
      <AnimatedSection delay={0.2}>
        <section id="results" className="results-section-new">
          <div className="container">
            <div className="section-eyebrow">PROVEN IMPACT</div>
            <h2 className="section-heading">Real Results, Not Just Features</h2>
            <p className="section-subheading">
              Measurable impact across your day — from messages to bookings.
            </p>
            
            {/* Example Impact Visual */}
            <div className="example-impact-card">
              <div className="impact-label">EXAMPLE IMPACT (ILLUSTRATIVE)</div>
              <div className="impact-visual-honest">
                <div className="impact-outcome">
                  <Zap size={18} className="outcome-icon" />
                  <div className="outcome-text">Faster replies</div>
                  <div className="outcome-bars">
                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '45%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '75%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.3 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '90%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.4 }} />

                  </div>
                </div>
                
                <div className="impact-outcome">
                  <Phone size={18} className="outcome-icon" />
                  <div className="outcome-text">Fewer missed calls</div>
                  <div className="outcome-bars">
                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '60%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.5 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '85%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.6 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '95%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.7 }} />

                  </div>
                </div>
                
                <div className="impact-outcome">
                  <Calendar size={18} className="outcome-icon" />
                  <div className="outcome-text">Fewer no-shows</div>
                  <div className="outcome-bars">
                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '50%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.8 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '70%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.9 }} />

                    <motion.div
                      className="outcome-bar"
                      initial={{ width: 0 }}
                      whileInView={{ width: '88%' }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 1.0 }} />

                  </div>
                </div>
              </div>
            </div>
            
            <div className="results-featured-new">
              <div className="featured-text">
                <h3>Reduce missed calls and convert more inquiries.</h3>
              </div>
              <div className="featured-metric">
                <div className="metric-ghost-bg">32</div>
                <div className="metric-main-value">
                  +<CountUp end={32} />%
                </div>
              </div>
            </div>
            <div className="results-grid-new">
              <div className="result-card-new">
                <div className="result-icon-new"><Phone size={22} /></div>
                <div className="result-value">+<CountUp end={45} />%</div>
                <p className="result-label">Inquiry response rate</p>
                <p className="result-note">(Example result)</p>
              </div>
              <div className="result-card-new">
                <div className="result-icon-new"><Calendar size={22} /></div>
                <div className="result-value">-<CountUp end={28} />%</div>
                <p className="result-label">No-show appointments</p>
                <p className="result-note">(Example result)</p>
              </div>
              <div className="result-card-new">
                <div className="result-icon-new"><Clock size={22} /></div>
                <div className="result-value"><CountUp end={2} />min</div>
                <p className="result-label">Average response time</p>
                <p className="result-note">(Example result)</p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Integrations */}
      <AnimatedSection delay={0.1}>
        <section id="integrations" className="integrations-section-new">
          <div className="container">
            <div className="section-eyebrow">COMPATIBLE WITH</div>
            <h2 className="section-heading">Integrations</h2>
            <div className="integrations-grid-new">
              <div className="integration-card-new">
                <MessageSquare size={26} />
                <span>WhatsApp</span>
              </div>
              <div className="integration-card-new">
                <MessageSquare size={26} />
                <span>Website Chat</span>
              </div>
              <div className="integration-card-new">
                <Calendar size={26} />
                <span>Google Calendar</span>
              </div>
              <div className="integration-card-new">
                <Calendar size={26} />
                <span>Calendly</span>
              </div>
              <div className="integration-card-new">
                <Mail size={26} />
                <span>Email/SMS</span>
              </div>
              <div className="integration-card-new">
                <Shield size={26} />
                <span>CRM (coming)</span>
              </div>
            </div>
            <p className="integrations-note">Integrations depend on your setup.</p>
          </div>
        </section>
      </AnimatedSection>

      {/* FAQ */}
      <AnimatedSection delay={0.15}>
        <section id="faq" className="faq-section">
          <div className="container-narrow">
            <div className="section-eyebrow center">COMMON QUESTIONS</div>
            <h2 className="section-heading">Frequently Asked Questions</h2>
            <div className="faq-list">
              {faqData.map((faq, index) =>
              <FAQItem
                key={index}
                question={faq.question}
                answer={faq.answer}
                isOpen={openFAQ === index}
                onClick={() => setOpenFAQ(openFAQ === index ? null : index)} />

              )}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Lead Form Section */}
      <AnimatedSection delay={0.1}>
        <section id="demo" className="demo-section">
          <div className="container-narrow">
            <div className="demo-card">
              <div className="section-eyebrow center">GET STARTED</div>
              <h2 className="demo-heading">Get your free demo</h2>
              <p className="demo-subheading">
                We'll show you a live example tailored to your clinic in under 15 minutes.
              </p>
              
              <form className="demo-form" onSubmit={handleSubmit}>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="fullName">Full Name *</label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Dr. Jane Smith"
                      required />

                  </div>
                  <div className="form-group">
                    <label htmlFor="clinicName">Clinic Name *</label>
                    <input
                      type="text"
                      id="clinicName"
                      name="clinicName"
                      value={formData.clinicName}
                      onChange={handleInputChange}
                      placeholder="Smith Family Clinic"
                      required />

                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="jane@smithclinic.com"
                      required />

                  </div>
                  <div className="form-group">
                    <label htmlFor="whatsapp">WhatsApp Number *</label>
                    <input
                      type="tel"
                      id="whatsapp"
                      name="whatsapp"
                      value={formData.whatsapp}
                      onChange={handleInputChange}
                      placeholder="+1 234 567 8900"
                      required />

                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="message">Message (Optional)</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Tell us about your clinic or any specific needs..."
                    rows={4}>
                  </textarea>
                </div>

                {formStatus.type === "success" ? (
  <div className="booking-success-card">
    <div className="booking-success-top">
      <span className="booking-pill">Step 1 complete</span>
      <span className="booking-subtle">Saved to your Leads sheet ✅</span>
    </div>

    <h3 className="booking-title">Step 2: Pick a time for your demo</h3>
    <p className="booking-desc">
      Choose any available slot. You’ll get a confirmation email (and a Meet link if enabled).
    </p>

    <div className="booking-actions">
      <button
        type="button"
        className="btn-primary booking-btn"
        onClick={openBooking}
      >
        Pick a time (opens calendar) →
      </button>

      <a
        className="booking-link"
        href={BOOKING_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open booking link in a new tab
      </a>
    </div>
  </div>
) : formStatus.message ? (
  <div className={`form-status ${formStatus.type}`}>
    {formStatus.message}
  </div>
) : null}


               <button
  type="submit"
  className="btn-primary btn-submit"
  disabled={isSubmitting || formStatus.type === "success"}
>


                  {isSubmitting ?
                  <>
                      <Loader2 size={18} className="spinner" />
                      Submitting...
                    </> :

                  <>
                   Submit & Pick a Time <ArrowRight size={18} />
                    </>
                  }
                </button>
              </form>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Footer */}
      <footer className="footer-new">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <div className="footer-logo">BookingReply</div>
              <p className="footer-tagline">AI receptionist for modern clinics</p>
            </div>
            <div className="footer-links-group">
              <button onClick={() => setActiveModal('privacy')} className="footer-link">Privacy</button>
              <button onClick={() => setActiveModal('terms')} className="footer-link">Terms</button>
              <button onClick={() => setActiveModal('contact')} className="footer-link">Contact</button>
            </div>
          </div>
          <div className="footer-bottom">
            <p className="footer-copyright">
              © {new Date().getFullYear()} BookingReply. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
      
      {/* Modals */}
      <Modal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Privacy Policy">
        <div className="modal-text">
          <p className="modal-updated">Last updated: 2026-01-12</p>
          
          <h3>Information We Collect</h3>
          <ul>
            <li>Name, clinic name, email address, and WhatsApp number when you request a demo</li>
            <li>Usage data and analytics to improve our services</li>
            <li>Communication preferences and interaction history</li>
          </ul>
          
          <h3>How We Use Your Information</h3>
          <ul>
            <li>Provide, maintain, and improve our AI receptionist services</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your inquiries and provide customer support</li>
            <li>Communicate about products, services, and updates</li>
          </ul>
          
          <h3>Data Security</h3>
          <ul>
            <li>Industry-standard encryption for data transmission</li>
            <li>Secure storage with regular backups</li>
            <li>Access controls and authentication protocols</li>
            <li>Regular security audits and updates</li>
          </ul>
          
          <h3>Contact Us</h3>
          <p>For privacy-related questions, contact us via the Contact modal or email hello@bookingreply.com</p>
        </div>
      </Modal>
      
      <Modal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Terms of Service">
        <div className="modal-text">
          <p className="modal-updated">Last updated: 2026-01-12</p>
          
          <h3>Acceptance of Terms</h3>
          <ul>
            <li>By using BookingReply, you agree to these Terms of Service</li>
            <li>Must comply with all applicable laws and regulations</li>
            <li>Services are provided "as is" without warranties</li>
          </ul>
          
          <h3>Service Description</h3>
          <ul>
            <li>AI-powered receptionist for administrative tasks</li>
            <li>Appointment booking, FAQs, and patient communications</li>
            <li>WhatsApp and website chat integration</li>
            <li>Setup typically completed in 48-72 hours</li>
          </ul>
          
          <h3>Service Level & Availability</h3>
          <ul>
            <li>Target uptime: 99.9% (not guaranteed)</li>
            <li>Scheduled maintenance windows as needed</li>
            <li>Support available during business hours</li>
          </ul>
          
          <h3>Cancellation & Billing</h3>
          <ul>
            <li>Month-to-month service, no long-term contracts</li>
            <li>Cancel anytime with written notice</li>
            <li>Pro-rated refunds not available</li>
          </ul>
          
          <h3>Changes to Terms</h3>
          <ul>
            <li>We may modify these terms at any time</li>
            <li>Continued use constitutes acceptance of changes</li>
            <li>Major changes will be communicated via email</li>
          </ul>
        </div>
      </Modal>
      
      <Modal isOpen={activeModal === 'contact'} onClose={() => setActiveModal(null)} title="Contact Us">
        <div className="modal-text">
          <h3>Get in Touch</h3>
          <p>We'd love to hear from you! Reach out for questions, support, or partnership inquiries.</p>
          
          <div className="contact-info">
            <div className="contact-item">
              <Mail size={20} />
              <div>
                <strong>Email</strong>
                <p><a href="mailto:hello@bookingreply.com" className="contact-link">hello@bookingreply.com</a></p>
              </div>
            </div>
            
            <div className="contact-item">
              <MessageSquare size={20} />
              <div>
                <strong>WhatsApp</strong>
                <p><a href="https://wa.me/15551234567" className="contact-link" target="_blank" rel="noopener noreferrer">+1 (555) 123-4567</a></p>
              </div>
            </div>
            
            <div className="contact-item">
              <Clock size={20} />
              <div>
                <strong>Business Hours</strong>
                <p>Monday - Friday: 9:00 AM - 6:00 PM EST</p>
              </div>
            </div>
          </div>
          
          <p className="contact-note">Or use the "Get Free Demo" button to schedule a personalized walkthrough.</p>
        </div>
      </Modal>
    </div>);

};

export default LandingPage;